import * as csstips from "csstips";
import * as React from "react";
import { style } from "typestyle";
import { MAP_ATTRIBUTION_HEIGHT_VAR } from "./MapContainerWithUiLayers";

type IJsxAny = React.JSX.Element | React.JSX.Element[] | null;

interface IMapControlsLayoutProps {
  topLeft?: IJsxAny;
  topCenter?: IJsxAny;
  topRight?: IJsxAny;
  middleLeft?: IJsxAny;
  middleCenter?: IJsxAny;
  middleRight?: IJsxAny;
  bottomLeft?: IJsxAny;
  bottomCenter?: IJsxAny;
  bottomRight?: IJsxAny;
}
export default function MapControlsLayout({
  topLeft,
  topCenter,
  topRight,
  middleLeft,
  middleCenter,
  middleRight,
  bottomLeft,
  bottomCenter,
  bottomRight,
}: IMapControlsLayoutProps) {
  return (
    <div
      className={style(
        csstips.vertical,
        csstips.fillParent,
        csstips.padding(8),
        { boxSizing: "border-box" },
      )}
    >
      <MapControlsRow left={topLeft} center={topCenter} right={topRight} />
      <div
        className={style(csstips.flex, {
          display: "flex",
          alignItems: "center",
          justifyContent: "left",
        })}
      >
        <MapControlsRow
          left={middleLeft}
          center={middleCenter}
          right={middleRight}
          additionalStyle={{ alignItems: "flex-end" }}
        />
      </div>
      <MapControlsRow
        left={bottomLeft}
        center={bottomCenter}
        right={bottomRight}
        // Wrap upwards so the left controls stay anchored to the bottom edge.
        // wrap-reverse also swaps the cross axis, so flex-start here is what
        // bottom-aligns items of different heights (flex-end without wrap).
        additionalStyle={{ alignItems: "flex-start", flexWrap: "wrap-reverse" }}
        // Only the bottom-right controls lift above the map's attribution line
        // (bottom-right, must never be covered; set by MapContainerWithUiLayers).
        // A relative offset does not change the layout, so opening the Esri
        // sources moves nothing else (bottom-left buttons, middle controls).
        rightStyle={{
          position: "relative",
          bottom: `var(${MAP_ATTRIBUTION_HEIGHT_VAR}, 0px)`,
        }}
      />
    </div>
  );
}

interface IMapControlsRowProps {
  left?: IJsxAny;
  center?: IJsxAny;
  right?: IJsxAny;
  additionalStyle?: object;
  rightStyle?: object;
}
const MapControlsRow = ({
  left,
  center,
  right,
  additionalStyle,
  rightStyle,
}: IMapControlsRowProps) => (
  <div
    className={style(
      csstips.content,
      csstips.horizontal,
      // When the controls don't fit (narrow screens), wrap instead of
      // overflowing: a row wider than the viewport makes mobile browsers zoom
      // the whole page out to fit it.
      { flexWrap: "wrap", rowGap: "8px" },
      additionalStyle || {},
    )}
  >
    <div
      className={style(
        csstips.content,
        csstips.horizontal,
        csstips.horizontallySpaced(8),
        // Groups wrap internally too, so several buttons in one slot (e.g. Show
        // Labels + Fit Site in topCenter on mobile) never widen the page.
        // flexShrink lets the group get narrower than its content so wrapping
        // can kick in; with enough space it keeps its natural width.
        { flexWrap: "wrap", rowGap: "8px", flexShrink: 1, minWidth: 0 },
      )}
    >
      {left ? left : null}
    </div>
    <div
      className={style(csstips.flex, csstips.horizontal, {
        justifyContent: "center",
        gap: "8px",
        flexWrap: "wrap",
      })}
    >
      {center ? center : null}
    </div>
    {/* marginLeft: auto keeps it right-aligned when wrapped onto its own line */}
    <div
      className={style(
        csstips.content,
        { marginLeft: "auto" },
        rightStyle || {},
      )}
    >
      {right ? right : null}
    </div>
  </div>
);
